import { Dispatch, MiddlewareAPI, createAction } from '@reduxjs/toolkit';
import {
  BroadcastWSAction,
  ChatWSAction,
  MeetingWSAction,
  RoomWSAction,
  isBroadcastWSAction,
  isChatWSAction,
  isMeetingWSAction,
  isRoomWSAction,
} from 'common/constants';
import { extractPathVariables, generateDynamicPath } from 'common/utils';
import { omit } from 'lodash-es';
import { SocketSendParams } from 'modules/webSocket/state';
import { websocketSend } from 'modules/webSocket/state/actions';
import { AppDispatch, RootState } from 'store';

type MeetingWSActionPath = MeetingWSAction | BroadcastWSAction | ChatWSAction | RoomWSAction;

/**
 * Factory function for creating Redux action creators for **Meetings** WebSocket actions.
 * @TODO Make it generic to handle other WS endpoints
 *
 * @template T - The type of data that will be sent with the WebSocket action.
 *
 * @param {MeetingWSAction} path - The path for the WebSocket action, which can contain variables indicated by a colon (e.g., '/meeting/:eventId/join').
 *
 * @param {T} data - The data to be sent with the WebSocket action.
 *
 * @example
 * ```ts
 * //before
 *
 * export const meetingJoin =
 * ({ eventId, pin }: { eventId: string; pin: string }) =>
 * (dispatch: AppDispatch) => {
 *   dispatch(
 *     websocketSend({
 *       path: `/meeting/${eventId}/join`,
 *       data: { pin: pin || undefined },
 *     })
 *   );
 * };
 *
 * // after
 * export const meetingJoin = createWebSocketAction<MeetingWSParams<MeetingJoinPayload>>('/meeting/:eventId/join');
 *
 * // usage in component or hook
 * const dispatch = useAppDispatch();
 * dispatch(meetingJoin({ eventId, pin: eventPinValue }));
 * ```
 *  */
export const createMeetingWSAction = <T extends Record<string, unknown>>(
  path: MeetingWSActionPath,
  callback?: (dispatch: Dispatch, data?: T) => void,
  config?: Partial<SocketSendParams>
) => {
  const someAction = createAction(path);
  const action = (data?: T) => (dispatch: Dispatch, getState: MiddlewareAPI<AppDispatch, RootState>['getState']) => {
    if (!(isMeetingWSAction(path) || isBroadcastWSAction(path) || isChatWSAction(path) || isRoomWSAction(path))) {
      throw Error(`Action '${path}' is not supported by Meeting WS`);
    }

    const { event } = getState();
    if (!event.activeEventId) return;

    const payload = omit(data, extractPathVariables(path));
    dispatch(
      websocketSend({
        path: generateDynamicPath(path, { eventId: event.activeEventId, ...data }),
        data: Object.keys(payload).length ? payload : undefined,
        plainBody: Object.keys(payload).length ? (config?.plainBody ?? true) : undefined,
      })
    );
    if (typeof callback === 'function') {
      callback(dispatch, data);
    }
    dispatch(someAction());
  };

  action.action = someAction;
  return action;
};
